import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import FilePreview from "./EmbedItems/FilePreview";
import { getFileIcon, truncateFileName } from "src/helpers/documentHelper";
import { useDownload } from "src/hooks/useDownload";
import { DownloadManager, makeKey } from "src/utils/downloadManager";
import { CircularProgress } from "src/components/shared/LoadingAnimations/CircularProgress";
import SlideOver from "src/components/shared/SlideOver";
import { formatFileSize } from "src/helpers/cmsHelper";
import { useParams } from "react-router-dom";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import moment from "moment";

export default function DocumentPreviewModal({ isOpen, onClose, docs, initialIndex = 0, isStorage = false }) {
  const { workspace_id, content_type, content_value, link_id } = useParams();
  const { form, page, group, document: document_id } = useUrlQueryParams();

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (!isOpen) return;
    const total = Array.isArray(docs) ? docs.length : 0;
    const start = Math.max(0, Math.min(initialIndex, Math.max(0, total - 1)));
    setCurrentIndex(start);
  }, [isOpen, docs, docs?.length, initialIndex]);

  const activeDoc = useMemo(() => {
    if (Array.isArray(docs) && docs.length > 0 && currentIndex !== -1) {
      return docs[Math.max(0, Math.min(currentIndex, docs.length - 1))];
    }
    return null;
  }, [docs, currentIndex]);

  const prevDoc = useMemo(() => {
    if (!Array.isArray(docs) || currentIndex <= 0) return null;
    return docs[currentIndex - 1];
  }, [docs, currentIndex]);

  const nextDoc = useMemo(() => {
    if (!Array.isArray(docs) || currentIndex >= docs.length - 1) return null;
    return docs[currentIndex + 1];
  }, [docs, currentIndex]);

  const total = Array.isArray(docs) ? docs.length : 1;
  const showNav = Array.isArray(docs) && total > 1;
  const canPrev = showNav && currentIndex > 0;
  const canNext = showNav && currentIndex < total - 1;

  const isReady = useMemo(() => {
    if (!isOpen || !activeDoc?._id) return false;
    if (isStorage) return true;
    return Boolean(workspace_id);
  }, [isOpen, workspace_id, activeDoc?._id, isStorage]);

  const dlKey = useMemo(() => {
    if (!activeDoc?._id) return null;
    if (isStorage) {
      return makeKey("storage-preview", { link_id: activeDoc?._id });
    }
    return makeKey("doc-preview", { ws: workspace_id, id: activeDoc?._id });
  }, [workspace_id, activeDoc?._id, isStorage]);

  const requestConfig = useMemo(() => {
    if (!isReady || !activeDoc?._id) return null;

    if (isStorage) {
      return {
        request: {
          method: "GET",
          url: "/portal/storage/:link_id/download/:storage_id",
          payload: {
            params: { link_id, storage_id: activeDoc?._id },
          },
          options: { responseType: "blob" },
        },
        cache: { ttlMs: 5 * 60 * 1000 },
      };
    }

    return {
      request: {
        method: "GET",
        url: "/portal/workspaces/:workspace_id/documents/:document_id/download",
        payload: {
          params: { workspace_id, document_id: activeDoc?._id },
          queries: {
            group,
            page,
            document: document_id,
            form,
            ...(content_type && content_value ? { [content_type]: content_value } : {}),
          },
        },
        options: { responseType: "blob" },
      },
      cache: { ttlMs: 5 * 60 * 1000 },
    };
  }, [isReady, activeDoc?._id, isStorage, workspace_id, group, page, document_id, form, content_type, content_value, link_id]);

  const isZip = activeDoc?.extension === "zip";

  const job = useDownload(dlKey, requestConfig, { autoStart: isReady && !isZip, startWhen: () => isReady && !isZip });

  const { status, progress, hasProgress, error, url, blob, name, extension, mime } = job || {};

  const downloadNow = () => {
    if (dlKey && requestConfig) {
      DownloadManager.start(dlKey, requestConfig);
    }
  };

  const handleNavigate = (dir) => {
    if (!showNav) return;
    if ((dir < 0 && !canPrev) || (dir > 0 && !canNext)) return;
    if (dlKey) DownloadManager.cancel(dlKey);
    setCurrentIndex((prev) => {
      const next = prev + dir;
      return Math.max(0, Math.min(next, total - 1));
    });
  };

  useEffect(() => {
    if (!isOpen || !showNav) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") handleNavigate(-1);
      if (e.key === "ArrowRight") handleNavigate(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showNav, currentIndex, dlKey, total]);

  const handleClose = () => {
    if (dlKey) DownloadManager.cancel(dlKey);
    setCurrentIndex(-1);
    onClose?.();
  };

  const file =
    status === "done" && url
      ? {
          name: name || activeDoc?.name || "file",
          type: mime || activeDoc?.mime_type || "",
          url,
          blob,
          extension: extension || activeDoc?.extension || "",
        }
      : null;

  const docExtension = activeDoc?.file?.extension || activeDoc?.extension || "";
  const docName = activeDoc?.name || "Unknown Document";
  const docSize = activeDoc?.size_in_bytes || activeDoc?.file?.size_in_bytes || 0;
  const docCreatedAt = activeDoc?.created_at;
  const docTags = activeDoc?.tags || [];

  const railRef = useRef(null);
  const dotRefs = useRef([]);

  const resolvedDocsCount = Array.isArray(docs) ? docs.length : 0;

  useEffect(() => {
    if (!isOpen || !railRef.current) return;
    const rail = railRef.current;
    const el = dotRefs.current[currentIndex];
    if (!el) return;
    const target = el.offsetLeft - (rail.clientWidth / 2 - el.offsetWidth / 2);
    rail.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [currentIndex, isOpen, resolvedDocsCount]);

  useEffect(() => {
    if (isZip && status === "done" && file?.url) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();

      return () => {
        URL.revokeObjectURL(file.url);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.url]);

  const headerComponent = (
    <div className="flex w-full items-start justify-between gap-1">
      <div className="grid w-full max-w-full">
        <div className="flex max-w-full items-center space-x-4 truncate py-1">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white shadow">{getFileIcon(docExtension)}</div>
          <div className="flex-shrink flex-grow truncate">
            <div className="flex items-center gap-3">
              <h2 className="truncate text-xl font-semibold text-gray-900">
                {docName}.{docExtension}
              </h2>
            </div>
            <p className="mt-1 text-xs text-gray-500 sm:text-sm">
              <span className="inline-flex items-center space-x-2">
                <span className="font-medium">{docExtension?.toUpperCase() || "UNKNOWN"}</span>
                <span>•</span>
                <span>{formatFileSize(docSize)}</span>
                {docCreatedAt && (
                  <>
                    <span>•</span>
                    <span>{moment(docCreatedAt).format("MMM D, YYYY, hh:mm A")}</span>
                  </>
                )}
              </span>
            </p>
          </div>
        </div>

        {docTags?.length > 0 && (
          <div className="ml-16 mt-2 flex flex-wrap items-center">
            <div className="flex flex-wrap gap-1">
              {docTags.map((tag, i) => (
                <span
                  key={i}
                  className="border-highlightColor/50 bg-highlightColor/10 inline-flex h-6 items-center justify-center rounded-full border px-2 text-[10px] font-medium text-highlightColor shadow-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleClose}
        className="rounded-xl p-2 text-gray-400 transition-colors duration-200 hover:bg-white hover:text-gray-600">
        <XMarkIcon className="h-6 w-6" />
      </button>
    </div>
  );

  const NavigationButton = ({ direction, doc, disabled, onClick }) => {
    if (!doc) return null;

    const isPrev = direction === "prev";
    const docExt = doc?.file?.extension || doc?.extension || "";
    const docName = doc?.name || "Unknown Document";

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`group relative flex-shrink overflow-hidden rounded-lg border border-gray-200 bg-white px-4 py-2 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 ${isPrev ? "mr-auto text-left" : "ml-auto text-right"}`}>
        <div className={`flex items-center gap-3 ${isPrev ? "" : "flex-row-reverse"}`}>
          <div className={`flex min-w-0 flex-col ${isPrev ? "items-start" : "items-start sm:items-end"}`}>
            <div className={`mb-1 text-xs text-gray-500 ${!isPrev ? "ml-auto sm:ml-[inherit]" : ""}`}>
              {isPrev ? (
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <ArrowLeftIcon className="h-4 w-4 text-gray-600 transition-colors group-hover:text-gray-800" /> Previous file
                </div>
              ) : (
                <div className="flex items-center gap-1 whitespace-nowrap">
                  Next file <ArrowRightIcon className="h-4 w-4 text-gray-600 transition-colors group-hover:text-gray-800" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">{getFileIcon(docExt, "h-4 w-4")}</div>
              <div className={`max-w-[100px] truncate text-sm font-medium text-gray-900 sm:max-w-none`}>{truncateFileName(docName, docExt, 25)}</div>
            </div>
          </div>
        </div>
      </button>
    );
  };

  const footerComponent = showNav ? (
    <div className="flex w-full items-center justify-between gap-4">
      {canPrev && prevDoc && (
        <NavigationButton
          direction="prev"
          doc={prevDoc}
          disabled={!canPrev}
          onClick={() => handleNavigate(-1)}
        />
      )}

      {canNext && nextDoc && (
        <NavigationButton
          direction="next"
          doc={nextDoc}
          disabled={!canNext}
          onClick={() => handleNavigate(1)}
        />
      )}
    </div>
  ) : null;

  return (
    <SlideOver
      isOpen={isOpen}
      handleClose={handleClose}
      position="right"
      headerClassName="!py-3"
      bodyClassName="!px-6 !pt-10 !pb-6"
      footerClassName="!px-6 !py-4"
      headerComponent={headerComponent}
      footerComponent={footerComponent}>
      <div className="flex h-full">
        <div className="mb-3 flex w-full flex-1 flex-col">
          {isZip ? (
            <div className={`flex h-full w-full flex-col items-center justify-center space-y-4`}>
              {status === "downloading" ? (
                <div className="flex flex-col items-center space-y-4">
                  {!hasProgress ? (
                    <CircularProgress
                      variant="spinner"
                      size={48}
                    />
                  ) : (
                    <CircularProgress
                      variant="progress"
                      value={progress}
                      size={48}
                    />
                  )}
                  <p className="text-gray-600">Downloading ZIP...</p>
                </div>
              ) : status === "done" && file ? (
                <a
                  href={file.url}
                  download={file.name}
                  className="flex items-center justify-center gap-x-2 rounded-lg bg-gray-200 px-4 py-2 text-highlightColor transition hover:bg-gray-100">
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Download ZIP
                </a>
              ) : (
                <>
                  <button
                    onClick={downloadNow}
                    className="flex items-center justify-center gap-x-2 rounded-lg bg-gray-200 px-4 py-2 text-highlightColor transition hover:bg-gray-100">
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Download ZIP
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {status === "downloading" ? (
                <div className="flex h-full flex-col items-center justify-center space-y-4">
                  {!hasProgress ? (
                    <CircularProgress
                      variant="spinner"
                      size={48}
                    />
                  ) : (
                    <CircularProgress
                      variant="progress"
                      value={progress}
                      size={48}
                    />
                  )}
                </div>
              ) : status === "error" ? (
                <div className="flex flex-col items-center justify-center space-y-6 py-12">
                  <div className="max-w-md text-center">
                    <h3 className="text-lg font-semibold text-gray-900">Preview Error</h3>
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm text-red-600">{error}</p>
                  </div>
                </div>
              ) : status === "done" && file ? (
                <div className="flex h-full w-full">
                  <FilePreview file={file} />
                </div>
              ) : status === "canceled" ? (
                <div className="flex h-[175px] items-center justify-center text-sm text-gray-500">Canceled</div>
              ) : (
                <div className="flex h-[175px] items-center justify-center text-sm text-gray-500">Waiting…</div>
              )}
            </>
          )}
        </div>
      </div>
    </SlideOver>
  );
}
